import { useState } from "react";
import { motion } from "framer-motion";
import { Check, ChevronDown, Server, Brain, Cloud, Shield } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { usePackageOfferings } from "@/providers/ContentProvider";
import { cn } from "@/lib/utils";
import type { ServiceSelectionData } from "./index";
import type { PackageOfferingDto } from "@shared/types/PackageOfferingDto";

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function getIconForOffering(offering: PackageOfferingDto) {
  const name = offering.name.toLowerCase();
  if (name.includes("mcp") || name.includes("gateway")) return Server;
  if (name.includes("knowledge") || name.includes("engine")) return Brain;
  if (name.includes("guardian")) return Shield;
  return Cloud;
}

interface ServiceSelectionStepProps {
  data: ServiceSelectionData;
  onChange: (data: ServiceSelectionData) => void;
}

export function ServiceSelectionStep({
  data,
  onChange,
}: ServiceSelectionStepProps) {
  const { offerings } = usePackageOfferings();
  const [packagesExpanded, setPackagesExpanded] = useState(true);

  const handlePackageSelect = (packageId: string) => {
    onChange({
      ...data,
      selectedPackageId: packageId,
      selectedVariantId: null,
    });
  };

  return (
    <Card className="glass-card border-gradient">
      <CardContent className="p-6 md:p-8 space-y-6">
        <div>
          <h2 className="font-display text-2xl font-bold mb-2">
            Select Your Service
          </h2>
          <p className="text-muted-foreground">
            Choose a service package that fits your needs, or build a custom
            solution.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <h3 className="font-semibold text-sm text-muted-foreground mb-2">
              Custom Solution
            </h3>
            <button
              onClick={() => handlePackageSelect("custom")}
              className={cn(
                "w-full text-left p-4 rounded-lg border-2 transition-all",
                data.selectedPackageId === "custom"
                  ? "border-primary bg-primary/10"
                  : "border-border hover:border-primary/50",
              )}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h4 className="font-semibold mb-1">Custom Solution</h4>
                  <p className="text-sm text-muted-foreground">
                    Build a tailored solution specific to your requirements
                  </p>
                </div>
                {data.selectedPackageId === "custom" && (
                  <Check className="h-5 w-5 text-primary shrink-0" />
                )}
              </div>
            </button>
          </div>

          <button
            onClick={() => setPackagesExpanded(!packagesExpanded)}
            className="w-full flex items-center justify-between p-4 bg-card/50 rounded-lg hover:bg-card/70 transition-colors"
          >
            <span className="font-semibold">Service Packages</span>
            <ChevronDown
              className={`h-5 w-5 transition-transform ${
                packagesExpanded ? "rotate-180" : ""
              }`}
            />
          </button>

          {packagesExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-3"
            >
              {offerings.map((offering) => {
                const Icon = getIconForOffering(offering);
                const isSelected = data.selectedPackageId === offering.id;

                return (
                  <button
                    key={offering.id}
                    onClick={() => handlePackageSelect(offering.id)}
                    className={cn(
                      "w-full text-left p-4 rounded-lg border-2 transition-all",
                      isSelected
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-primary/50",
                    )}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <Icon className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold">{offering.name}</h4>
                            <Badge variant="secondary" className="text-xs">
                              {offering.timelineEstimate}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">
                            {offering.description}
                          </p>
                          {offering.pricing.priceRange && (
                            <p className="text-sm font-semibold text-primary">
                              {formatCurrency(offering.pricing.priceRange.min)}
                              {offering.pricing.type === "RECURRING"
                                ? "/mo"
                                : ""}{" "}
                              USD
                            </p>
                          )}
                        </div>
                      </div>
                      {isSelected && (
                        <Check className="h-5 w-5 text-primary shrink-0" />
                      )}
                    </div>
                  </button>
                );
              })}
            </motion.div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
